import { useMutation, useQueryClient } from '@tanstack/react-query';
import { reorderMeetingTemplates } from '@wundo/client/memberMeetingTemplates.ts';
import type {
  MeetingTemplateItem,
  MeetingTemplateScope,
} from '@wundo/shared/meetingTemplate.ts';
import { orderTemplateSection } from '@/lib/orderTemplateSection.ts';

/** A scope's templates in their new order. */
export interface MeetingTemplateReorder {
  scope: MeetingTemplateScope;
  ids: string[];
}

/*
 * The new order shows before the request is answered: the row has already
 * moved under the pointer. A refused save puts the old order back, and the
 * refetch has the last word either way.
 */
export function useMeetingTemplateReorder(userId: string | undefined) {
  const queryClient = useQueryClient();
  const key = ['meetingTemplates', userId, 'manage'];

  return useMutation({
    mutationFn: ({ scope, ids }: MeetingTemplateReorder) =>
      reorderMeetingTemplates(userId!, scope, ids),
    onMutate: async ({ scope, ids }) => {
      await queryClient.cancelQueries({ queryKey: ['meetingTemplates'] });
      const previous = queryClient.getQueryData<MeetingTemplateItem[]>(key);

      if (previous !== undefined) {
        queryClient.setQueryData<MeetingTemplateItem[]>(
          key,
          orderTemplateSection(previous, scope, ids),
        );
      }

      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(key, context.previous);
      }
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['meetingTemplates'] }),
  });
}
